import { useTranslate, useMany } from '@refinedev/core';
import { List, DeleteButton } from '@refinedev/antd';
import { Table, Space, Tag, Form, Row, Col, Input } from 'antd';
import { LinkOutlined, SearchOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import type { OAuthTokenOut, UserOut } from '@/api/generated/schemas';
import { useTableSearch } from '@/hooks/useTableSearch';
import { useTableRowNavigateToShow } from '@/hooks/useTableRowNavigateToShow';
import { useAdminTable } from '@/hooks/useAdminTable';
import type { CrudFilter } from '@refinedev/core';
import { TextFilter } from '@/components/common/TextFilter';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';
import { isFieldFiltered } from '@/utils/filters';
import { getSortOrder } from '@/utils/sorters';

export const OAuthTokenList = () => {
  const t = useTranslate();
  const rowNav = useTableRowNavigateToShow();
  const { onSearch, createDebouncedSubmit } = useTableSearch();

  const { tableProps, searchFormProps, sorters, setFilters, filters } =
    useAdminTable<OAuthTokenOut>({
      pagination: { pageSize: 20 },
      resource: 'iam.oauth_tokens',
      onSearch,
      sorters: {
        initial: [{ field: 'created_at', order: 'desc' }],
      },
    });

  const debouncedSubmit = createDebouncedSubmit(searchFormProps.form);

  const userIds = [
    ...new Set(
      (tableProps.dataSource ?? []).map((r) => r.user_id).filter(Boolean)
    ),
  ];

  const { result: usersResult } = useMany<UserOut>({
    resource: 'iam.users',
    ids: userIds,
    queryOptions: { enabled: userIds.length > 0 },
  });

  const usersById = Object.fromEntries(
    (usersResult?.data ?? []).map((u: UserOut) => [u.id, u])
  );

  return (
    <List>
      <Form {...searchFormProps} layout="vertical">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={16} md={12} lg={8}>
            <Form.Item name="q" style={{ marginBottom: 0 }}>
              <Input
                placeholder={t('iam.oauth_tokens.search.placeholder')}
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => debouncedSubmit(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Table {...rowNav('iam.oauth_tokens', tableProps)} rowKey="id">
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
        />

        <Table.Column
          dataIndex="client_id"
          title={t('iam.oauth_tokens.fields.client_id')}
        />

        <Table.Column
          dataIndex="user_id"
          title={t('iam.oauth_tokens.fields.user_id')}
          render={(userId: number) => {
            const user = usersById[userId];
            return (
              <Space size={8}>
                <span>{user ? (user.email ?? user.username) : userId}</span>
                <Link to={`/iam/users/${userId}`}>
                  <LinkOutlined />
                </Link>
              </Space>
            );
          }}
          filtered={isFieldFiltered(filters as CrudFilter[], 'user_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <TextFilter
              field="user_id"
              width={120}
              placeholder={t('iam.oauth_tokens.fields.user_id')}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="token_type"
          title={t('iam.oauth_tokens.fields.token_type')}
          render={(value: string) => value || 'Bearer'}
        />

        <Table.Column
          dataIndex="revoked"
          title={t('iam.oauth_tokens.fields.revoked')}
          render={(value: boolean) => (
            <Tag color={value ? 'red' : 'green'}>
              {value
                ? t('iam.oauth_tokens.status.revoked')
                : t('iam.oauth_tokens.status.active')}
            </Tag>
          )}
        />

        <Table.Column
          dataIndex="created_at"
          title={t('base.fields.created_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'created_at')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'created_at')}
          filterDropdown={({ confirm, clearFilters }) => (
            <DateRangeFilter
              field="created_at"
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: number) =>
            value ? new Date(value).toLocaleString() : '-'
          }
        />

        <Table.Column
          title={t('actions.column')}
          dataIndex="actions"
          render={(_, record: OAuthTokenOut) => (
            <Space>
              <DeleteButton hideText size="small" recordItemId={record.id} />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
